import {
	IcoBluetooth,
	IcoCamera,
	IcoReclinedSeat,
	IcoTv,
	IcoWifi
} from "assets/icons";
import IcoTrunk from "assets/icons/ico-trunk";
import { Button, DocumentItem, Input, Select, ToggleSwitch } from "components";
import { HeaderWithBack } from "components/HeaderBack";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { getPartnerUuid } from "services/auth";
import {
	createOrUpdateVehicle,
	getVehicle,
	getVehicleDocument,
	getVehicleModels,
	getVehiclePhoto,
	uploadVehicleDocument,
	uploadVehiclePhoto
} from "services/vehicle";
import * as Yup from "yup";
import styles from "./styles.module.scss";

const VehicleSchema = Yup.object().shape({
	model: Yup.string().required("Campo obrigatório"),
	color: Yup.string().required("Campo obrigatório"),
	seats: Yup.string()
		.required("Campo obrigatório")
		.matches(/^\d+$/, "Campo inválido"),
	plate_number: Yup.string()
		.required("Campo obrigatório")
		.matches(/^([A-Z]{3}-?\d{4} *|[A-Z]{3}\d[A-Z]\d{2} *)$/, "Campo inválido"),
	photo: Yup.mixed<File>()
		.when("photo_uploaded", {
			is: (value: string | null) => !value,
			then: (schema) => schema.required("Campo obrigatório"),
			otherwise: (schema) => schema.notRequired()
		})
		.nullable(),
	insurance_company: Yup.string().required("Campo obrigatório"),
	insurance_policy_number: Yup.string().required("Campo obrigatório"),
	insurance_validity: Yup.date().required("Campo obrigatório"),
	artesp_authorization: Yup.mixed<File>().nullable(),
	antt_authorization: Yup.mixed<File>().nullable(),
	cadastur_certificate: Yup.mixed<File>().nullable(),
	special_auth: Yup.mixed<File>().nullable()
});

type UpsertVehicleFormValues = {
	id: string;
	model: string;
	color: string;
	seats: number | undefined;
	plate_number: string;
	has_reclining_seats: boolean;
	has_tv: boolean;
	has_minibar: boolean;
	has_wifi: boolean;
	has_bluetooth: boolean;
	has_trunk: boolean;
	photo: File | null;
	photo_uploaded: string | null;
	insurance_company: string;
	insurance_policy_number: string;
	insurance_validity: string;
	artesp_authorization: File | null;
	artesp_authorization_uploaded: string | null;
	antt_authorization: File | null;
	antt_authorization_uploaded: string | null;
	cadastur_certificate: File | null;
	cadastur_certificate_uploaded: string | null;
	special_auth: File | null;
	special_auth_uploaded: string | null;
};

export default function UpsertVehiclePage() {
	const navigate = useNavigate();

	const [partnerId, setPartnerId] = useState("");
	const [models, setModels] = useState<any[]>([]);

	const [hasReclinedSeat, setHasReclinedSeat] = useState<boolean>(false);
	const [hasTv, setHasTv] = useState<boolean>(false);
	const [hasFrigobar, setHasFrigobar] = useState<boolean>(false);
	const [hasWifi, setHasWifi] = useState<boolean>(false);
	const [hasBluetooth, setHasBluetooth] = useState<boolean>(false);
	const [hasTrunk, setHasTrunk] = useState<boolean>(false);

	const [selectedCar, setSelectedCar] = useState("");
	const [selectedColor, setSelectedColor] = useState("");
	const [selectedSecurity, setSelectedSecurity] = useState("");

	const [submitting, setSubmitting] = useState<boolean>(false);

	const [vehicle, setVehicle] = useState<UpsertVehicleFormValues>({
		id: "",
		model: "",
		color: "",
		seats: undefined,
		plate_number: "",
		has_reclining_seats: false,
		has_tv: false,
		has_minibar: false,
		has_wifi: false,
		has_bluetooth: false,
		has_trunk: false,
		photo: null,
		photo_uploaded: null,
		insurance_company: "",
		insurance_policy_number: "",
		insurance_validity: "",
		artesp_authorization: null,
		artesp_authorization_uploaded: null,
		antt_authorization: null,
		antt_authorization_uploaded: null,
		cadastur_certificate: null,
		cadastur_certificate_uploaded: null,
		special_auth: null,
		special_auth_uploaded: null
	});

	const colors: Record<string, string> = {
		PRETA: "Preta",
		BRANCA: "Branca",
		PRATA: "Prata",
		AZUL: "Azul"
	};

	const params = useParams();
	const vehicleId = params.vehicleId || null;

	const fetchData = async () => {
		const partnerUuid = (await getPartnerUuid()) || "";
		setPartnerId(partnerUuid);

		const result: any[] = (await getVehicleModels()) || [];
		setModels(result);

		const apiVehicle: UpsertVehicleFormValues =
			(await getVehicle(partnerUuid, vehicleId)) || vehicle;

		const photos: any =
			(await getVehiclePhoto(partnerUuid, params.vehicleId)) || null;
		const imageUrl = photos[0].image.split("/");
		const imageName = imageUrl[imageUrl.length - 1];
		apiVehicle.photo_uploaded = imageName;

		const documents: any =
			(await getVehicleDocument(partnerUuid, params.vehicleId)) || null;
		for (let d = 0; d < documents.length; d += 1) {
			const document = documents[d];
			const documentUrl = document.file?.split("/") || [];
			const documentName = documentUrl[documentUrl.length - 1];
			switch (document.document_type) {
				case 1:
					apiVehicle.artesp_authorization_uploaded = documentName;
					break;
				case 2:
					apiVehicle.antt_authorization_uploaded = documentName;
					break;
				case 3:
					apiVehicle.special_auth_uploaded = documentName;
					break;
				case 4:
					apiVehicle.cadastur_certificate_uploaded = documentName;
					break;
				default:
					break;
			}
		}

		setVehicle(apiVehicle);
	};

	useEffect(() => {
		fetchData();
	}, []);

	const handleDocumentSearch = (documentType: string) => {
		console.log("Buscar documento:", documentType);
	};

	const formik = useFormik<UpsertVehicleFormValues>({
		initialValues: vehicle,
		validationSchema: VehicleSchema,
		onSubmit: async (form) => {
			setSubmitting(true);

			const hasArtesp = !!(
				form.artesp_authorization || form.artesp_authorization_uploaded
			);
			const hasAntt = !!(
				form.antt_authorization || form.antt_authorization_uploaded
			);

			if (!hasArtesp && !hasAntt) {
				formik.setErrors({
					...formik.errors,
					artesp_authorization:
						"É obrigatório enviar pelo menos um dos documentos: ARTESP ou ANTT.",
					antt_authorization:
						"É obrigatório enviar pelo menos um dos documentos: ARTESP ou ANTT."
				});
				setSubmitting(false);
				return;
			}

			if (!form.photo && !form.photo_uploaded) {
				setSubmitting(false);
				return;
			}

			const formattedValidity = form.insurance_validity
				? new Date(form.insurance_validity).toISOString().slice(0, 10)
				: "";

			const vehicleResult: any = await createOrUpdateVehicle(
				partnerId,
				vehicleId,
				{
					...form,
					insurance_validity: formattedValidity
				}
			).catch((error) => {
				setSubmitting(false);
				let message = "Algo saiu errado. ";

				if (error.code < 500) {
					message += "Verifique os dados digitados e tente novamente.";
					if (error.body.model) formik.errors.model = error.body.model;
					if (error.body.color) formik.errors.color = error.body.color;
					if (error.body.seats) formik.errors.seats = error.body.seats;
					if (error.body.plate_number) {
						if (
							error.body.plate_number[0] ===
								"Veículo with this plate number already exists." ||
							error.body.plate_number[0] ===
								"Veículo com este plate number já existe."
						)
							formik.errors.plate_number = "Veículo com esta placa já existe";
						else formik.errors.plate_number = error.body.plate_number;
					}
					if (error.body.has_reclining_seats)
						formik.errors.has_reclining_seats = error.body.has_reclining_seats;
					if (error.body.has_tv) formik.errors.has_tv = error.body.has_tv;
					if (error.body.has_minibar)
						formik.errors.has_minibar = error.body.has_minibar;
					if (error.body.has_wifi) formik.errors.has_wifi = error.body.has_wifi;
					if (error.body.has_bluetooth)
						formik.errors.has_bluetooth = error.body.has_bluetooth;
					if (error.body.insurance_company)
						formik.errors.insurance_company = error.body.insurance_company;
					if (error.body.insurance_policy_number)
						formik.errors.insurance_policy_number =
							error.body.insurance_policy_number;
					if (error.body.insurance_validity)
						formik.errors.insurance_validity = error.body.insurance_validity;
				} else {
					message += "Entre em contato com o suporte.";
				}

				toast.error(message, {
					position: "bottom-center",
					autoClose: 5000,
					hideProgressBar: true,
					closeOnClick: true,
					pauseOnHover: true,
					draggable: true,
					progress: undefined,
					theme: "light"
				});
			});

			const vehicle_id = vehicleResult?.id;
			if (!vehicle_id) {
				return;
			}

			let uploads = true;

			if (form.photo) {
				uploads =
					uploads &&
					((await uploadVehiclePhoto(
						vehicle_id,
						form.photo,
						"Foto do veículo"
					).catch((error) => {
						setSubmitting(false);
						let message = "Algo saiu errado. ";

						if (error.code < 500) {
							message += "Verifique os dados digitados e tente novamente.";
							if (error.body.title) formik.errors.photo = error.body.title;
							if (error.body.image) formik.errors.photo = error.body.image;
						} else {
							message += "Entre em contato com o suporte.";
						}

						toast.error(message, {
							position: "bottom-center",
							autoClose: 5000,
							hideProgressBar: true,
							closeOnClick: true,
							pauseOnHover: true,
							draggable: true,
							progress: undefined,
							theme: "light"
						});
					})) ||
						false);
			}

			if (form.artesp_authorization) {
				uploads =
					uploads &&
					((await uploadVehicleDocument(
						vehicle_id,
						form.artesp_authorization,
						"1"
					).catch((error) => {
						setSubmitting(false);
						let message = "Algo saiu errado. ";

						if (error.code < 500) {
							message += "Verifique os dados digitados e tente novamente.";
							if (error.body.document_type)
								formik.errors.artesp_authorization = error.body.document_type;
							if (error.body.file)
								formik.errors.artesp_authorization = error.body.file;
						} else {
							message += "Entre em contato com o suporte.";
						}

						toast.error(message, {
							position: "bottom-center",
							autoClose: 5000,
							hideProgressBar: true,
							closeOnClick: true,
							pauseOnHover: true,
							draggable: true,
							progress: undefined,
							theme: "light"
						});
					})) ||
						false);
			}

			if (form.antt_authorization) {
				uploads =
					uploads &&
					((await uploadVehicleDocument(
						vehicle_id,
						form.antt_authorization,
						"2"
					).catch((error) => {
						setSubmitting(false);
						let message = "Algo saiu errado. ";

						if (error.code < 500) {
							message += "Verifique os dados digitados e tente novamente.";
							if (error.body.document_type)
								formik.errors.antt_authorization = error.body.document_type;
							if (error.body.file)
								formik.errors.antt_authorization = error.body.file;
						} else {
							message += "Entre em contato com o suporte.";
						}

						toast.error(message, {
							position: "bottom-center",
							autoClose: 5000,
							hideProgressBar: true,
							closeOnClick: true,
							pauseOnHover: true,
							draggable: true,
							progress: undefined,
							theme: "light"
						});
					})) ||
						false);
			}

			if (form.cadastur_certificate) {
				uploads =
					uploads &&
					((await uploadVehicleDocument(
						vehicle_id,
						form.cadastur_certificate,
						"4"
					).catch((error) => {
						setSubmitting(false);
						let message = "Algo saiu errado. ";

						if (error.code < 500) {
							message += "Verifique os dados digitados e tente novamente.";
							if (error.body.document_type)
								formik.errors.cadastur_certificate = error.body.document_type;
							if (error.body.file)
								formik.errors.cadastur_certificate = error.body.file;
						} else {
							message += "Entre em contato com o suporte.";
						}

						toast.error(message, {
							position: "bottom-center",
							autoClose: 5000,
							hideProgressBar: true,
							closeOnClick: true,
							pauseOnHover: true,
							draggable: true,
							progress: undefined,
							theme: "light"
						});
					})) ||
						false);
			}

			if (form.special_auth) {
				uploads =
					uploads &&
					((await uploadVehicleDocument(
						vehicle_id,
						form.special_auth,
						"3"
					).catch((error) => {
						setSubmitting(false);
						let message = "Algo saiu errado. ";

						if (error.code < 500) {
							message += "Verifique os dados digitados e tente novamente.";
							if (error.body.document_type)
								formik.errors.special_auth = error.body.document_type;
							if (error.body.file) formik.errors.special_auth = error.body.file;
						} else {
							message += "Entre em contato com o suporte.";
						}

						toast.error(message, {
							position: "bottom-center",
							autoClose: 5000,
							hideProgressBar: true,
							closeOnClick: true,
							pauseOnHover: true,
							draggable: true,
							progress: undefined,
							theme: "light"
						});
					})) ||
						false);
			}

			if (uploads) navigate("/cadastro/veiculos");
		},
		enableReinitialize: true
	});

	return (
		<div className={styles.container}>
			<HeaderWithBack label="Novo veículo" backTo={-1} />
			<form onSubmit={formik.handleSubmit} className={styles.form}>
				<div className={styles.upsertContent}>
					<Select
						id="model"
						name="model"
						label="Modelo/Veículo"
						placeholder="Escolha um modelo"
						value={formik.values.model}
						onChange={(newValue) => {
							formik.setFieldValue("model", newValue);
						}}
						error={formik.touched.model && formik.errors.model}
						errorMessage={formik.errors.model}
						options={models
							.filter((model) => model.name !== "Indiferente")
							.map((model) => ({
								value: model.id,
								label: model.name
							}))}
					/>
					<Select
						id="color"
						name="color"
						label="Cor"
						placeholder="Escolha uma cor"
						value={formik.values.color}
						onChange={(newValue) => {
							formik.setFieldValue("color", newValue);
						}}
						error={formik.touched.color && formik.errors.color}
						errorMessage={formik.errors.color}
						options={Object.keys(colors).map((k) => ({
							value: k,
							label: colors[k]
						}))}
					/>
					<Input
						id="seats"
						name="seats"
						placeholder="Quantidade de assentos"
						label="Nº de Assentos"
						value={formik.values.seats}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.seats && formik.errors.seats}
						errorMessage={formik.errors.seats}
					/>
					<Input
						id="plate_number"
						name="plate_number"
						placeholder="Ex.: ABC1234 | ABC1D23"
						label="Placa"
						value={formik.values.plate_number}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.plate_number && formik.errors.plate_number}
						errorMessage={formik.errors.plate_number}
					/>
					<span className={styles.categoryTitle}>Comodidades</span>
					<ToggleSwitch
						label="Banco reclinável / Leito"
						icon={<IcoReclinedSeat width="40" />}
						value={formik.values.has_reclining_seats}
						onChange={(newValue) => {
							formik.setFieldValue("has_reclining_seats", newValue);
						}}
					/>
					<ToggleSwitch
						label="TV"
						icon={<IcoTv width="40" />}
						value={formik.values.has_tv}
						onChange={(newValue) => {
							formik.setFieldValue("has_tv", newValue);
						}}
					/>
					{/*
					<ToggleSwitch
						label="Frigobar"
						icon={<IcoFrigobar width="40" />}
						value={formik.values.has_minibar}
						onChange={(newValue) => {
							formik.setFieldValue("has_minibar", newValue);
						}}
					/>
					*/}
					<ToggleSwitch
						label="Wi-fi"
						icon={<IcoWifi width="40" />}
						value={formik.values.has_wifi}
						onChange={(newValue) => {
							formik.setFieldValue("has_wifi", newValue);
						}}
					/>
					<ToggleSwitch
						label="Bluetooth"
						icon={<IcoBluetooth width="40" />}
						value={formik.values.has_bluetooth}
						onChange={(newValue) => {
							formik.setFieldValue("has_bluetooth", newValue);
						}}
					/>
					<ToggleSwitch
						label="Bagageiro"
						icon={<IcoTrunk width="40" />}
						value={formik.values.has_trunk}
						onChange={(newValue) => {
							formik.setFieldValue("has_trunk", newValue);
						}}
					/>
					<Button
						type="button"
						variant="outline"
						onClick={() => {
							document.getElementById("vehicle-photo-input")?.click();
						}}
					>
						<div className={styles.iconButton}>
							<IcoCamera />
						</div>
						Fotos do veículo
					</Button>
					{formik.values.photo && formik.values.photo.name && (
						<div className={styles.centeredFileName}>
							<span className={styles.fileName}>
								{formik.values.photo.name}
							</span>
						</div>
					)}
					{formik.values.photo_uploaded && (
						<div className={styles.centeredFileName}>
							<span className={styles.fileName}>
								{formik.values.photo_uploaded}
							</span>
						</div>
					)}
					{formik.errors.photo && (
						<div className={styles.centeredFileName}>
							<span className={styles.error}>{formik.errors.photo}</span>
						</div>
					)}
					<input
						id="vehicle-photo-input"
						type="file"
						accept="image/*"
						style={{ display: "none" }}
						onChange={(e) => {
							const file = e.target.files?.[0];
							if (file) {
								formik.setFieldValue("photo", file);
							}
						}}
					/>
					<span className={styles.categoryTitle}>Seguro Viagem</span>
					<Input
						id="insurance_company"
						name="insurance_company"
						label="Seguradora"
						placeholder="Digite o nome da seguradora"
						maxLength={50}
						value={formik.values.insurance_company}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={
							formik.touched.insurance_company &&
							formik.errors.insurance_company
						}
						errorMessage={formik.errors.insurance_company}
					/>
					<Input
						id="insurance_policy_number"
						name="insurance_policy_number"
						label="Número da Apólice"
						placeholder="Digite sua apólice"
						maxLength={50}
						value={formik.values.insurance_policy_number}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={
							formik.touched.insurance_policy_number &&
							formik.errors.insurance_policy_number
						}
						errorMessage={formik.errors.insurance_policy_number}
					/>
					<Input
						id="insurance_validity"
						name="insurance_validity"
						type="date"
						placeholder="dd/mm/aaaa"
						label="Período de Vigência"
						value={formik.values.insurance_validity}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={
							formik.touched.insurance_validity &&
							formik.errors.insurance_validity
						}
						errorMessage={formik.errors.insurance_validity}
					/>
					<span className={styles.categoryTitle}>Licenças e Documentações</span>
					<DocumentItem
						title={
							<>
								Autorização ARTESP
								<br />
								{formik.values.artesp_authorization &&
									formik.values.artesp_authorization.name && (
										<span className={styles.fileName}>
											&nbsp;&nbsp;-&nbsp;&nbsp;
											{formik.values.artesp_authorization.name}
										</span>
									)}
								{formik.values.artesp_authorization_uploaded && (
									<span className={styles.fileName}>
										&nbsp;&nbsp;-&nbsp;&nbsp;
										{formik.values.artesp_authorization_uploaded}
									</span>
								)}
								{formik.errors.artesp_authorization && (
									<span className={styles.error}>
										&nbsp;&nbsp;-&nbsp;&nbsp;
										{formik.errors.artesp_authorization}
									</span>
								)}
							</>
						}
						onSearch={() => document.getElementById("artesp-input")?.click()}
					/>
					<input
						id="artesp-input"
						type="file"
						accept="application/pdf"
						style={{ display: "none" }}
						onChange={(e) => {
							const file = e.target.files?.[0];
							if (file) {
								formik.setFieldValue("artesp_authorization", file);
							}
						}}
					/>
					<DocumentItem
						title={
							<>
								Autorização ANTT
								<br />
								{formik.values.antt_authorization &&
									formik.values.antt_authorization.name && (
										<span className={styles.fileName}>
											&nbsp;&nbsp;-&nbsp;&nbsp;
											{formik.values.antt_authorization.name}
										</span>
									)}
								{formik.values.antt_authorization_uploaded && (
									<span className={styles.fileName}>
										&nbsp;&nbsp;-&nbsp;&nbsp;
										{formik.values.antt_authorization_uploaded}
									</span>
								)}
								{formik.errors.antt_authorization && (
									<span className={styles.error}>
										&nbsp;&nbsp;-&nbsp;&nbsp;
										{formik.errors.antt_authorization}
									</span>
								)}
							</>
						}
						onSearch={() => document.getElementById("antt-input")?.click()}
					/>
					<input
						id="antt-input"
						type="file"
						accept="application/pdf"
						style={{ display: "none" }}
						onChange={(e) => {
							const file = e.target.files?.[0];
							if (file) {
								formik.setFieldValue("antt_authorization", file);
							}
						}}
					/>
					<DocumentItem
						title={
							<>
								Certificado Cadastur
								<br />
								{formik.values.cadastur_certificate &&
									formik.values.cadastur_certificate.name && (
										<span className={styles.fileName}>
											&nbsp;&nbsp;-&nbsp;&nbsp;
											{formik.values.cadastur_certificate.name}
										</span>
									)}
								{formik.values.cadastur_certificate_uploaded && (
									<span className={styles.fileName}>
										&nbsp;&nbsp;-&nbsp;&nbsp;
										{formik.values.cadastur_certificate_uploaded}
									</span>
								)}
								{formik.errors.cadastur_certificate && (
									<span className={styles.error}>
										&nbsp;&nbsp;-&nbsp;&nbsp;
										{formik.errors.cadastur_certificate}
									</span>
								)}
							</>
						}
						onSearch={() => document.getElementById("cadastur-input")?.click()}
					/>
					<input
						id="cadastur-input"
						type="file"
						accept="application/pdf"
						style={{ display: "none" }}
						onChange={(e) => {
							const file = e.target.files?.[0];
							if (file) {
								formik.setFieldValue("cadastur_certificate", file);
							}
						}}
					/>
					<DocumentItem
						title={
							<>
								Autorizações Especiais
								<br />
								{formik.values.special_auth &&
									formik.values.special_auth.name && (
										<span className={styles.fileName}>
											&nbsp;&nbsp;-&nbsp;&nbsp;
											{formik.values.special_auth.name}
										</span>
									)}
								{formik.values.special_auth_uploaded && (
									<span className={styles.fileName}>
										&nbsp;&nbsp;-&nbsp;&nbsp;
										{formik.values.special_auth_uploaded}
									</span>
								)}
								{formik.errors.special_auth && (
									<span className={styles.error}>
										&nbsp;&nbsp;-&nbsp;&nbsp;
										{formik.errors.special_auth}
									</span>
								)}
							</>
						}
						onSearch={() =>
							document.getElementById("special-auth-input")?.click()
						}
					/>
					<input
						id="special-auth-input"
						type="file"
						accept="application/pdf"
						style={{ display: "none" }}
						onChange={(e) => {
							const file = e.target.files?.[0];
							if (file) {
								formik.setFieldValue("special_auth", file);
							}
						}}
					/>
				</div>
				<div className={styles.buttonWrapper}>
					<Button type="submit">Salvar</Button>
				</div>
			</form>
		</div>
	);
}
